import type { StatutCandidature } from './candidature.js';
import type { CandidatureResume } from './dossiers.js';

/** Au-delà, un candidat jamais appelé est signalé (délai de premier contact). */
export const DELAI_PREMIER_CONTACT_HEURES = 48;
/** Marge après l'heure du rendez-vous avant de réclamer la saisie de l'entretien. */
export const MARGE_ENTRETIEN_HEURES = 2;

export type CleSignal =
  | 'appel_en_cours'
  | 'a_relancer'
  | 'rappel_prevu'
  | 'injoignable'
  | 'sans_contact'
  | 'rdv_prevu'
  | 'entretien_a_saisir'
  | 'justificatifs_retard'
  | 'justificatifs_attendus'
  | 'a_reevaluer'
  | 'cv_illisible'
  | 'plusieurs_postes'
  | 'vivier'
  | 'ne_plus_contacter';

export interface Signal {
  cle: CleSignal;
  libelle: string;
  niveau: 'info' | 'alerte';
  /** Échéance à afficher à côté du libellé, au format de l'écran. */
  date?: string;
}

const EN_ATTENTE_D_APPEL: readonly StatutCandidature[] = ['nouveau', 'a_contacter', 'contacte'];
const AVANT_CONTACT: readonly StatutCandidature[] = ['nouveau', 'a_contacter'];
/** Un dossier clos n'attend plus de pièce : ni alerte ni rappel. */
const DOSSIER_CLOS: readonly StatutCandidature[] = ['embauche', 'ecarte', 'desistement', 'vivier'];
const HEURE = 3_600_000;

function jourLisible(jour: string): string {
  return jour.split('-').reverse().join('/');
}

/**
 * Badges et alertes d'une candidature (docs/UI.md § Badges et alertes).
 */
export function signaux(c: CandidatureResume, maintenant: Date = new Date()): Signal[] {
  const t = maintenant.getTime();
  const liste: Signal[] = [];

  if (c.contactOuvert) {
    liste.push({ cle: 'appel_en_cours', libelle: 'Contact non clôturé', niveau: 'alerte' });
  }

  // Les rappels n'ont de sens que tant qu'aucun rendez-vous n'est pris.
  if (EN_ATTENTE_D_APPEL.includes(c.statut)) {
    if (c.rappelLe && new Date(c.rappelLe).getTime() <= t) {
      liste.push({ cle: 'a_relancer', libelle: 'Rappel à faire', niveau: 'alerte', date: c.rappelLe });
    } else if (c.rappelLe) {
      liste.push({ cle: 'rappel_prevu', libelle: 'Rappel prévu', niveau: 'info', date: c.rappelLe });
    } else if (c.dernierJoignable === false) {
      liste.push({ cle: 'injoignable', libelle: 'Injoignable au dernier appel', niveau: 'alerte' });
    }
  }

  if (AVANT_CONTACT.includes(c.statut) && c.nbContacts === 0) {
    const attente = t - new Date(c.recueLe).getTime();
    if (attente > DELAI_PREMIER_CONTACT_HEURES * HEURE) {
      const jours = Math.floor(attente / (24 * HEURE));
      liste.push({
        cle: 'sans_contact',
        libelle: `Sans contact depuis ${jours} jour${jours > 1 ? 's' : ''}`,
        niveau: 'alerte',
      });
    }
  }

  if (c.statut === 'rdv_planifie' && c.rdvLe) {
    if (new Date(c.rdvLe).getTime() + MARGE_ENTRETIEN_HEURES * HEURE < t) {
      liste.push({
        cle: 'entretien_a_saisir',
        libelle: 'Entretien passé, évaluation à saisir',
        niveau: 'alerte',
        date: c.rdvLe,
      });
    } else {
      liste.push({ cle: 'rdv_prevu', libelle: 'Rendez-vous', niveau: 'info', date: c.rdvLe });
    }
  }

  // Compte à rebours des justificatifs (sept jours après la charte signée).
  if (!DOSSIER_CLOS.includes(c.statut)) {
    if (c.justificatifsEnRetard > 0) {
      liste.push({
        cle: 'justificatifs_retard',
        libelle: `${c.justificatifsEnRetard} justificatif${c.justificatifsEnRetard > 1 ? 's' : ''} en retard`,
        niveau: 'alerte',
      });
    } else if (c.echeanceJustificatifs) {
      liste.push({
        cle: 'justificatifs_attendus',
        libelle: `Justificatifs avant le ${jourLisible(c.echeanceJustificatifs)}`,
        niveau: 'info',
      });
    }
  }

  // Règle 4 : le signal tombe dès qu'une nouvelle évaluation est postérieure.
  if (c.profilModifieLe && c.noteLe && new Date(c.profilModifieLe) > new Date(c.noteLe)) {
    liste.push({ cle: 'a_reevaluer', libelle: 'Profil modifié depuis l’évaluation', niveau: 'alerte' });
  }
  if (c.cvIllisible) {
    liste.push({ cle: 'cv_illisible', libelle: 'CV illisible', niveau: 'alerte' });
  }
  if (c.nbCandidaturesPersonne > 1) {
    liste.push({
      cle: 'plusieurs_postes',
      libelle: `Candidat sur ${c.nbCandidaturesPersonne} postes`,
      niveau: 'info',
    });
  }
  if (c.issueDuVivier) {
    liste.push({ cle: 'vivier', libelle: 'Issu du vivier', niveau: 'info' });
  }
  if (c.nePlusContacter) {
    liste.push({ cle: 'ne_plus_contacter', libelle: 'Ne plus contacter', niveau: 'alerte' });
  }
  return liste;
}

/** Filtre rapide « à relancer » de la liste par annonce. */
export function aRelancer(c: CandidatureResume, maintenant: Date = new Date()): boolean {
  return signaux(c, maintenant).some((s) => s.cle === 'a_relancer' || s.cle === 'injoignable');
}
